'use client'

import Dinero from 'dinero.js'
import IntlMessageFormat from 'intl-messageformat'
import { CheckIcon, HeadsetIcon } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'nextjs-toploader/app'
import { type HTMLAttributes, useState } from 'react'
import { RainbowButton } from '@/components/magicui/rainbow-button'
import { TextSeparator } from '@/components/misc/text-separator'
import { Button, buttonVariants } from '@/components/ui/button'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { config } from '@/config'
import { Entitlement, type ProductWithMetadata } from '@/lib/polar/types'
import { cn } from '@/utils/cn'
import { getAbsoluteUrl } from '@/utils/url'
import { useProduct } from '../hooks'
import type { TPricingPlan, TPricingPlanFeature } from '../types'

Dinero.defaultCurrency = 'USD'
Dinero.globalLocale = 'en-US'

interface PricingPlanItemProps extends HTMLAttributes<HTMLDivElement> {
  plan: TPricingPlan
  interval: 'month' | 'year'
  mode: 'landing' | 'app'
}

export function PricingPlanItem({
  plan,
  interval,
  mode,
  className,
  ...props
}: PricingPlanItemProps) {
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const isEnterprise = plan.entitlement === Entitlement.ENTERPRISE
  const product = useProduct(plan.entitlement, interval)
  if (!product && !isEnterprise) return null

  // Calculate Pricing
  let monthlyPrice = Dinero({ amount: product?.fixedPrice.priceAmount ?? 0 })
  if (interval === 'year') monthlyPrice = monthlyPrice.divide(12)
  const monthlyPriceFormatted = isEnterprise ? 'Custom' : monthlyPrice.toFormat('$0,0.00')

  let yearlyPrice = Dinero({ amount: product?.fixedPrice.priceAmount ?? 0 })
  if (interval === 'month') yearlyPrice = yearlyPrice.multiply(12)
  const yearlyPriceFormatted = yearlyPrice.toFormat('$0,0.00')

  // Handle plan click
  const handlePlanClick = () => {
    setIsLoading(true)

    const checkoutUrl = new URL(getAbsoluteUrl(`/api/checkout/${product?.metadata.slug}`))

    // Determine referral id (if set)
    const referralId =
      typeof window !== 'undefined'
        ? (window as Window & { affonso_referral?: string }).affonso_referral
        : undefined
    if (referralId) {
      checkoutUrl.searchParams.set('referral_id', referralId)
    }

    router.push(checkoutUrl.toString())
  }

  return (
    <div
      className={cn(
        'relative z-10 box-border grid h-full w-full grid-rows-[165px_1fr_auto] rounded-xl border bg-background p-3 text-foreground shadow-lg outline-2 outline-focus outline-offset-2 transition-transform-background motion-reduce:transition-none dark:border dark:border-neutral-400/15',
        plan.isPopular ? 'border-2 border-neutral-900 dark:border-neutral-100' : '',
        className,
      )}
      {...props}
    >
      <CardHeader className="h-full border-b p-4">
        <CardTitle className="flex items-center justify-between pb-2">
          <span className="font-bold font-display">
            {product?.metadata.displayName || plan.name}
          </span>
          {plan.isPopular && (
            <RainbowButton
              className="pointer-events-none h-auto whitespace-nowrap rounded-full px-2.5 py-0.5 font-semibold text-xs"
              type="button"
            >
              Most Powerful
            </RainbowButton>
          )}
        </CardTitle>

        {/* <motion.div
          className="flex grow flex-col justify-center text-3xl"
          key={monthlyPriceFormatted}
          initial={{ opacity: 0, filter: 'blur(5px)' }}
          animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
        > */}
        <div className="flex grow flex-col justify-center text-3xl">
          <div>
            <span className="font-bold">{monthlyPriceFormatted}</span>
            <span className="ml-1 font-medium text-muted-foreground text-xs">/ month</span>
          </div>

          {interval === 'year' && !isEnterprise && (
            <p className="py-1 font-medium text-muted-foreground text-xs">
              Billed as {yearlyPriceFormatted} / year
            </p>
          )}

          <div className="pt-2 font-medium text-foreground text-sm">{plan.description}</div>
        </div>
      </CardHeader>

      <CardContent className="flex h-full flex-col justify-start p-4 pt-5">
        <ul className="space-y-2">
          {plan.features.map((feature, featureIndex) => (
            <PricingPlanFeature key={featureIndex} feature={feature} product={product} />
          ))}
        </ul>
      </CardContent>

      {isEnterprise && (
        <a
          href={config.links.calEnterprise}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonVariants({ size: 'lg', variant: 'secondary' })}
        >
          <HeadsetIcon />
          Book a Call
        </a>
      )}

      {!isEnterprise && mode === 'app' && (
        <Button
          size="lg"
          variant={plan.isPopular ? 'default' : 'secondary'}
          className={cn('mt-4 w-full')}
          isLoading={isLoading}
          onClick={handlePlanClick}
        >
          Subscribe
        </Button>
      )}

      {!isEnterprise && mode === 'landing' && (
        <Link
          href="/app"
          className={buttonVariants({
            size: 'lg',
            variant: plan.isPopular ? 'default' : 'secondary',
            className: 'mt-4 w-full',
          })}
        >
          Subscribe
        </Link>
      )}
    </div>
  )
}

interface PricingPlanFeatureProps {
  feature: TPricingPlanFeature
  product: ProductWithMetadata | null
}
function PricingPlanFeature({
  feature: { feature: _feature, subtitle: _subtitle, isComingSoonSeparator },
  product,
}: PricingPlanFeatureProps) {
  if (isComingSoonSeparator) {
    return <TextSeparator text="Coming soon" className="py-2" />
  }
  if (!_feature) {
    return null
  }

  let feature = _feature
  let subtitle = _subtitle
  if (product) {
    feature = new IntlMessageFormat(feature || '', 'en-US').format(product.metadata).toString()
    subtitle = new IntlMessageFormat(subtitle || '', 'en-US').format(product.metadata).toString()
  }

  return (
    <li className="flex items-center">
      <CheckIcon className="mr-2 size-4 shrink-0 text-green-500" />
      <div className="flex flex-col">
        <span className="font-medium dark:font-normal">{feature}</span>
        {subtitle && <span className="font-medium text-muted-foreground text-xs">{subtitle}</span>}
      </div>
    </li>
  )
}
